import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { partnerRulesApi, retailerApi } from '../../services/api';
import { RuleFields, retailerName, toPayload } from './shared';

// New partner rule (Phase 4a, plan §3.4): the builder's three steps plus an owner picker. The
// rule is on as soon as it is saved — no preview, no delay (plan §0).

export default function NewPartnerRule() {
  const navigate = useNavigate();
  const [retailers, setRetailers] = useState([]);
  const [owner, setOwner] = useState('');
  const [value, setValue] = useState({});
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    retailerApi.list().then((d) => setRetailers(d.retailers || [])).catch((e) => setError(e.message));
  }, []);

  const { payload, missing } = owner ? toPayload(value) : { payload: null, missing: 'Choose the owner.' };
  const ownerName = retailerName(retailers.find((r) => r.retailer_id === owner));

  const turnOn = async () => {
    setBusy(true);
    setError(null);
    try {
      const row = await partnerRulesApi.create({ owner_retailer_id: owner, reason_note: note || null, ...payload });
      navigate(`/partner-rules/${row.id}`);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 900 }}>
      <Box>
        <Button size="small" onClick={() => navigate('/partner-rules')}>
          ← Partner rules
        </Button>
        <Typography variant="h4">New rule</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <TextField
            select
            label="Owner"
            size="small"
            value={owner}
            onChange={(e) => {
              setOwner(e.target.value);
              setValue({});
            }}
            sx={{ maxWidth: 360 }}
          >
            {retailers
              .filter((r) => ['active', 'testing'].includes(r.status))
              .map((r) => (
                <MenuItem key={r.retailer_id} value={r.retailer_id}>
                  {retailerName(r)}
                </MenuItem>
              ))}
          </TextField>

          {owner && (
            <RuleFields ownerId={owner} ownerName={ownerName} value={value} onChange={setValue} retailers={retailers} />
          )}

          <TextField
            label="Note (optional, for HandOff)"
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Call with the owner, 3 Nov"
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              {missing || 'The rule starts at once, on both sides. You can stop it at any time.'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button onClick={() => navigate('/partner-rules')}>Cancel</Button>
              <Button variant="contained" disabled={!payload || busy} onClick={turnOn}>
                Turn on rule
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
